type AnchorTarget = HTMLElement & { name?: string };

const prefersReducedMotion = () =>
	window.matchMedia?.( '(prefers-reduced-motion: reduce)' )?.matches ?? false;

const isSamePageHashLink = ( a: HTMLAnchorElement ): string | null => {
	const href = a.getAttribute( 'href' );
	if ( ! href ) {
		return null;
	}

	// Only care about links that have a hash
	const hashIndex = href.indexOf( '#' );
	if ( hashIndex === -1 ) {
		return null;
	}

	// Handle plain "#section"
	if ( href.startsWith( '#' ) ) {
		return href.slice( 1 );
	}

	// Handle "/path#section" or "https://site/path#section"
	let url: URL;
	try {
		url = new URL( href, window.location.href );
	} catch {
		return null;
	}

	// Only same-origin + same-path links should be hijacked
	if ( url.origin !== window.location.origin ) {
		return null;
	}
	if (
		url.pathname.replace( /\/+$/, '' ) !==
		window.location.pathname.replace( /\/+$/, '' )
	) {
		return null;
	}

	return url.hash ? url.hash.slice( 1 ) : null;
};

const findAnchorTarget = ( rawId: string ): AnchorTarget | null => {
	const id = decodeURIComponent( rawId ).trim();
	if ( ! id ) {
		return null;
	}

	// Standard: id="..."
	const byId = document.getElementById( id ) as AnchorTarget | null;
	if ( byId ) {
		return byId;
	}

	// Legacy anchors: <a name="...">
	const byName = document.querySelector< AnchorTarget >(
		`a[name="${ CSS.escape( id ) }"]`
	);
	return byName ?? null;
};

const getScrollMarginTopPx = ( el: Element ): number => {
	const cs = window.getComputedStyle( el );
	const v = cs.scrollMarginTop; // respects scroll-margin-top if set
	const n = Number.parseFloat( v );
	return Number.isFinite( n ) ? n : 0;
};

const smoothScrollTo = ( target: HTMLElement ) => {
	// Respect scroll-margin-top by subtracting it from the final scroll position.
	const marginTop = getScrollMarginTopPx( target );
	const y = window.scrollY + target.getBoundingClientRect().top - marginTop;

	window.scrollTo( {
		top: y,
		behavior: prefersReducedMotion() ? 'auto' : 'smooth',
	} );

	// Move focus for accessibility without jumping the page
	if ( ! target.hasAttribute( 'tabindex' ) ) {
		target.setAttribute( 'tabindex', '-1' );
	}
	target.focus( { preventScroll: true } );
};

export const attachJumpLinks = ( links: HTMLAnchorElement[] ) => {
	for ( const a of links ) {
		const hashId = isSamePageHashLink( a );
		if ( ! hashId ) {
			continue;
		}

		const target = findAnchorTarget( hashId );
		if ( ! target ) {
			continue;
		}

		a.addEventListener( 'click', ( e ) => {
			// Allow modifier-click behavior (new tab, etc.)
			if ( e.defaultPrevented ) {
				return;
			}
			if ( e.button !== 0 ) {
				return;
			}
			if ( e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ) {
				return;
			}

			e.preventDefault();

			// Update URL hash (without jumping)
			const newHash = `#${ encodeURIComponent(
				decodeURIComponent( hashId )
			) }`;

			if ( window.location.hash !== newHash ) {
				history.pushState(
					null,
					'',
					`${ window.location.pathname }${ window.location.search }${ newHash }`
				);
			}
			smoothScrollTo( target );
		} );
	}
};
